import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import AdminLayout from "@/layouts/AdminLayout";
import DashboardPage from "@/pages/DashboardPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import CalendarPage from "@/pages/CalendarPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import QuotesPage from "@/pages/QuotesPage";
import QuoteDetailPage from "@/pages/QuoteDetailPage";
import PaymentsPage from "@/pages/PaymentsPage"; // legacy redirect
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import BillingCyclePage from "@/pages/BillingCyclePage";
import CatalogPage from "@/pages/CatalogPage";
import LeadsPage from "@/pages/LeadsPage";
import FinancePage from "@/pages/FinancePage";
import GalleryPage from "@/pages/GalleryPage";
import LandingPage from "@/pages/public/LandingPage";
import TechApp from "@/pages/tech/TechApp";
import TechLogin from "@/pages/tech/TechLogin";
import VisitReviewPage from "@/pages/VisitReviewPage";

function ProtectedRoute({ children, requireAdmin = false, requireTech = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div data-testid="auth-loading" className="font-mono-kpi text-sm text-neutral-500 uppercase tracking-widest">
          Verificando sesión…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const isTech = user.role === "technician";
  if (requireTech && !isTech && user.role !== "admin") return <Navigate to="/admin" replace />;
  if (!requireTech && isTech) return <Navigate to="/tecnicos" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to={user.role === "technician" ? "/tecnicos" : "/admin"} replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <PublicOnly>
                <LoginPage />
              </PublicOnly>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="clientes" element={<CustomersPage />} />
            <Route path="clientes/:id" element={<CustomerDetailPage />} />
            <Route path="calendario" element={<CalendarPage />} />
            <Route path="revision-visitas" element={<VisitReviewPage />} />
            <Route path="revision-visitas/:id" element={<VisitReviewPage />} />
            <Route path="ciclo" element={<BillingCyclePage />} />
            <Route path="servicios/:id" element={<ServiceDetailPage />} />
            <Route path="cotizaciones" element={<QuotesPage />} />
            <Route path="cotizaciones/:id" element={<QuoteDetailPage />} />
            <Route path="cobros" element={<PaymentsPage />} />
            <Route path="finanzas" element={<FinancePage />} />
            <Route path="catalogo" element={<CatalogPage />} />
            <Route path="galeria" element={<GalleryPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="usuarios" element={<UsersPage />} />
            <Route path="ajustes" element={<SettingsPage />} />
          </Route>

          <Route
            path="/tecnicos/login"
            element={
              <PublicOnly>
                <TechLogin />
              </PublicOnly>
            }
          />
          <Route
            path="/tecnicos/*"
            element={
              <ProtectedRoute requireTech>
                <TechApp />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
